import { X } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { COLORS, MOTION, SHADOW, SPACE, TYPE } from "../theme/index.js";
import { IconButton } from "./IconButton.js";

type DrawerWidth = "default" | "wide" | "extra-wide";

type DrawerProps = {
  open: boolean;
  onClose: () => void;
  width?: DrawerWidth;
  title?: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
};

const WIDTHS: Record<DrawerWidth, number> = { default: 480, wide: 640, "extra-wide": 720 };

export function Drawer({
  open,
  onClose,
  width = "default",
  title,
  subtitle,
  actions,
  children,
}: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  const w = WIDTHS[width];
  return (
    <>
      <button
        type="button"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 50,
          border: "none",
          padding: 0,
          cursor: "default",
        }}
        onClick={onClose}
        aria-label="Close panel"
      />
      {/* biome-ignore lint/a11y/useSemanticElements: aside used for slide-in drawer panel; open state managed by parent */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: w,
          background: COLORS.bg,
          borderLeft: `1px solid ${COLORS.border}`,
          zIndex: 51,
          display: "flex",
          flexDirection: "column",
          boxShadow: SHADOW.panel,
          animation: `slideInRight ${MOTION.slow} ${MOTION.ease}`,
        }}
      >
        <header
          style={{
            padding: SPACE[5],
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            gap: SPACE[4],
          }}
        >
          <div>
            {title && (
              <h2
                style={{
                  fontSize: TYPE.size.xl,
                  fontWeight: TYPE.weight.semibold,
                  color: COLORS.text,
                  margin: 0,
                }}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <div
                style={{
                  fontSize: TYPE.size.sm,
                  color: COLORS.textMuted,
                  margin: 0,
                  marginTop: SPACE[2],
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: SPACE[2], flexShrink: 0 }}>
            {actions}
            <IconButton onClick={onClose} aria-label="Close">
              <X size={18} />
            </IconButton>
          </div>
        </header>
        <div style={{ flex: 1, overflowY: "auto" }}>{children}</div>
      </aside>
    </>
  );
}
